import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Box,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Breadcrumbs,
  InputBase,
  Divider,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Sidebar from './Sidebar';
import NotificationCenter from './NotificationCenter';
import { useAuth } from '../hooks/useAuth';
import { toggleSidebar, selectSidebarOpen } from '../features/ui/uiSlice';
import { useTour } from '../context/TourContext';

const EXPANDED_WIDTH = 260;
const MINI_WIDTH = 64;

// Map all 36 routes to breadcrumb segments
const ROUTE_BREADCRUMBS = {
  '/':                              ['Dashboard'],
  '/advanced-analytics':            ['Overview', 'Analytics'],
  '/devices':                       ['Endpoints', 'Devices'],
  '/deployments':                   ['Endpoints', 'Deployments'],
  '/remote':                        ['Endpoints', 'Remote Management'],
  '/updates':                       ['Endpoints', 'Updates'],
  '/edr':                           ['Threat Defense', 'EDR'],
  '/threat-intelligence':           ['Threat Defense', 'Threat Intelligence'],
  '/dlp':                           ['Threat Defense', 'DLP'],
  '/forensics':                     ['Threat Defense', 'Forensics'],
  '/remediation':                   ['Threat Defense', 'Remediation'],
  '/application-control':           ['Threat Defense', 'Application Control'],
  '/device-control':                ['Threat Defense', 'Device Control'],
  '/nas-protection':                ['Threat Defense', 'NAS Protection'],
  '/policies':                      ['Compliance & Policy', 'Policies'],
  '/security':                      ['Compliance & Policy', 'Security'],
  '/compliance':                    ['Compliance & Policy', 'Compliance'],
  '/health':                        ['Compliance & Policy', 'Health'],
  '/reports':                       ['Operations', 'Reports'],
  '/configurations':                ['Operations', 'Configurations'],
  '/logs':                          ['Operations', 'Logs'],
  '/backups':                       ['Operations', 'Backups'],
  '/users':                         ['Administration', 'Users'],
  '/tenant-management':             ['Administration', 'Tenant Management'],
  '/settings/api-keys':             ['Administration', 'API Keys'],
  '/webhooks':                      ['Administration', 'Webhooks'],
  '/integrations':                  ['Administration', 'Integrations'],
  '/api-docs':                      ['Administration', 'API Docs'],
  '/settings':                      ['Administration', 'Settings'],
  '/settings/2fa':                  ['Administration', 'Settings', '2FA Setup'],
  '/settings/notifications':        ['Administration', 'Settings', 'Notifications'],
  '/settings/notification-templates': ['Administration', 'Settings', 'Notification Templates'],
};

function getBreadcrumbs(pathname) {
  // Exact match first
  if (ROUTE_BREADCRUMBS[pathname]) return ROUTE_BREADCRUMBS[pathname];
  // Dynamic route fallback
  if (pathname.startsWith('/devices/') && pathname.includes('/inventory')) return ['Endpoints', 'Devices', 'Inventory'];
  if (pathname.startsWith('/devices/') && pathname.includes('/platform'))  return ['Endpoints', 'Devices', 'Platform'];
  return ['Dashboard'];
}

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const sidebarOpen = useSelector(selectSidebarOpen);
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [searchFocused, setSearchFocused] = React.useState(false);
  const { startTour } = useTour();

  const drawerWidth = sidebarOpen ? EXPANDED_WIDTH : MINI_WIDTH;
  const breadcrumbs = getBreadcrumbs(location.pathname);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => { logout(); handleMenuClose(); };

  const displayName = user?.username || user?.email?.split('@')[0] || 'User';
  const avatarLetter = displayName.charAt(0).toUpperCase();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          transition: 'width 200ms cubic-bezier(0.4,0,0.6,1), margin 200ms cubic-bezier(0.4,0,0.6,1)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: '56px !important' }}>
          {/* Hamburger */}
          <Tooltip title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
            <IconButton size="small" onClick={() => dispatch(toggleSidebar())} sx={{ color: 'text.secondary' }}>
              <MenuIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Breadcrumbs */}
          <Breadcrumbs
            separator={<NavigateNextIcon sx={{ fontSize: 14 }} />}
            sx={{ flex: 1, display: { xs: 'none', sm: 'flex' } }}
          >
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                <Typography
                  key={crumb}
                  variant="body2"
                  sx={{
                    fontWeight: isLast ? 600 : 400,
                    color: isLast ? 'text.primary' : 'text.secondary',
                    fontSize: '0.82rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {crumb}
                </Typography>
              );
            })}
          </Breadcrumbs>

          {/* Spacer on xs */}
          <Box sx={{ flex: 1, display: { xs: 'flex', sm: 'none' } }} />

          {/* Global search */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.04)',
              border: '1px solid',
              borderColor: searchFocused ? 'primary.main' : 'rgba(255,255,255,0.08)',
              borderRadius: 5,
              px: 1.5,
              py: 0.5,
              gap: 1,
              width: searchFocused ? 280 : 180,
              transition: 'width 250ms ease, border-color 150ms ease',
              display: { xs: 'none', md: 'flex' },
            }}
          >
            <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <InputBase
              placeholder="Search..."
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              sx={{ fontSize: '0.82rem', color: 'text.primary', '& input::placeholder': { color: 'text.secondary', opacity: 1 } }}
            />
          </Box>

          {/* System status indicator */}
          <Box
            sx={{
              display: { xs: 'none', lg: 'flex' },
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              bgcolor: 'rgba(76,175,80,0.08)',
              border: '1px solid rgba(76,175,80,0.2)',
              borderRadius: 5,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: 'success.main',
                flexShrink: 0,
                '@keyframes pulse': {
                  '0%': { boxShadow: '0 0 0 0 rgba(76,175,80,0.5)' },
                  '70%': { boxShadow: '0 0 0 5px rgba(76,175,80,0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(76,175,80,0)' },
                },
                animation: 'pulse 2.5s infinite',
              }}
            />
            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, letterSpacing: '0.05em', fontSize: '0.68rem' }}>
              PROTECTED
            </Typography>
          </Box>

          {/* Help / Tour */}
          <Tooltip title="Take a guided tour">
            <IconButton
              data-tour="tour-button"
              size="small"
              onClick={startTour}
              sx={{ color: 'text.secondary' }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          {user && (
            <Box data-tour="notification-center">
              <NotificationCenter />
            </Box>
          )}

          {/* User identity */}
          {user && (
            <>
              <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 0.5 }} />
              <Box data-tour="user-menu" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: '0.82rem', color: 'text.primary' }}>
                    {displayName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.62rem' }}>
                    {user.role}
                  </Typography>
                </Box>
                <Tooltip title="Account menu">
                  <IconButton size="small" onClick={handleMenuOpen} sx={{ p: 0.25 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        background: 'linear-gradient(135deg, #00b4d8 0%, #7c4dff 100%)',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        color: '#fff',
                      }}
                    >
                      {avatarLetter}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              </Box>

              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem disabled sx={{ opacity: '1 !important' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{displayName}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{user.email}</Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { navigate('/settings'); handleMenuClose(); }}>
                  <PersonIcon sx={{ mr: 1.5, fontSize: 18 }} />
                  <Typography variant="body2">Profile & Settings</Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  <LogoutIcon sx={{ mr: 1.5, fontSize: 18 }} />
                  <Typography variant="body2">Sign Out</Typography>
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Sidebar />

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: 3,
          width: `calc(100% - ${drawerWidth}px)`,
          transition: 'width 200ms cubic-bezier(0.4,0,0.6,1)',
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: '56px !important' }} />
        <Box
          sx={{
            '& > *': {
              animation: 'fadeInUp 250ms ease-out',
              '@keyframes fadeInUp': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to:   { opacity: 1, transform: 'translateY(0)' },
              },
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

export default Layout;
